import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AuthState } from "~/types/auth";
import {
  IMPERSONATION_AUTH_STORAGE_KEY,
  isImpersonationTab,
} from "~/lib/impersonation-tab";

/** Shared, cross-tab persisted session (watched by axios.config's cross-tab sync). */
const SHARED_AUTH_STORAGE_KEY = "palate-auth";

export { IMPERSONATION_AUTH_STORAGE_KEY };

// GH-923 — decided once, BEFORE the store's first hydration (see
// isImpersonationTab): a support-mode tab reads and writes only its own
// sessionStorage key, from the first load of the handoff through any reload.
const IS_IMPERSONATION_TAB_AT_BOOT = isImpersonationTab();

// ============================================================================
// Store
// ============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      sessionId: null,
      role: null,
      userId: null,
      email: null,
      shell: null,
      profile: null,
      isInitializing: true, // Default to true
      selectedLocationId: null,

      setAuth: (params) => {
        const currentState = get();
        set({
          token: params.token !== undefined ? params.token : currentState.token,
          refreshToken:
            params.refreshToken !== undefined
              ? params.refreshToken
              : currentState.refreshToken,
          sessionId:
            params.sessionId !== undefined
              ? params.sessionId
              : currentState.sessionId,
          role: params.role !== undefined ? params.role : currentState.role,
          userId:
            params.userId !== undefined ? params.userId : currentState.userId,
          email: params.email !== undefined ? params.email : currentState.email,
          shell: params.shell !== undefined ? params.shell : currentState.shell,
        });
      },

      setProfile: (profile) => {
        // Dev-harness predictability: when VITE_DEV_BYPASS_AUTH is on, strip the
        // employee record so the seeded owner-who-also-has-an-employee-record lands
        // in the admin shell instead of /employee/home. Production builds never set
        // this flag, so the live behavior is unchanged.
        if (
          profile &&
          import.meta.env.VITE_DEV_BYPASS_AUTH === "true" &&
          profile.employee
        ) {
          const { employee: _employee, ...adminProfile } = profile;
          set({ profile: adminProfile });
          return;
        }
        set({ profile });
      },

      setInitializing: (isInitializing) => set({ isInitializing }),

      setSelectedLocationId: (selectedLocationId) =>
        set({ selectedLocationId }),

      clearAuth: () => {
        set({
          token: null,
          refreshToken: null,
          sessionId: null,
          role: null,
          userId: null,
          email: null,
          shell: null,
          profile: null,
          selectedLocationId: null,
          // Do not reset isInitializing on logout
        });
      },

      isAuthenticated: () => Boolean(get().token),
    }),
    {
      // GH-923 — an impersonation tab never touches the shared key, not even
      // to hydrate from it.
      name: IS_IMPERSONATION_TAB_AT_BOOT
        ? IMPERSONATION_AUTH_STORAGE_KEY
        : SHARED_AUTH_STORAGE_KEY,
      storage: createJSONStorage(() =>
        IS_IMPERSONATION_TAB_AT_BOOT ? sessionStorage : localStorage,
      ),
      // Only persist the tokens and essential user info
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        sessionId: state.sessionId,
        role: state.role,
        userId: state.userId,
        email: state.email,
        shell: state.shell,
        profile: state.profile,
        selectedLocationId: state.selectedLocationId,
      }),
    },
  ),
);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get auth token from store (client-side)
 */
export const getAuthToken = () => useAuthStore.getState().token;

// Server-side getter removed as access token is no longer in cookie

// ============================================================================
// Impersonation tab-scoping (GH-923)
// ============================================================================

/**
 * GH-923 — an Ops Panel "Enter as <Restaurant>" impersonation session must
 * never touch the shared "palate-auth" localStorage key. That key drives
 * admin#922's cross-tab sync (axios.config.ts initCrossTabAuthSync): ANY
 * write to it fires a `storage` event in every other open tab of the same
 * browser, and a different session showing up there (which an impersonation
 * token always is — a different `sub`/`type` claim from the operator's own)
 * makes handleAuthStorageEvent treat it as "this tab's session changed
 * elsewhere" and supersede + reload into it. Before this fix, starting
 * impersonation in one tab silently turned every other open admin tab into
 * the impersonated restaurant.
 *
 * Call this ONCE, from root.tsx's AuthInitializer, BEFORE the impersonation
 * token is written to the store (the clearAuth()/setAuth() that follows it).
 * The store normally already starts scoped — isImpersonationTab
 * (~/lib/impersonation-tab) sees the same URL param before the first
 * hydration — so this is the explicit, idempotent guarantee at the write
 * site. It points THIS tab's zustand `persist` writes at a distinct
 * sessionStorage key instead of the shared localStorage one:
 *   - sessionStorage is per tab: never shared with another open tab, so a
 *     write here never reaches another tab's `storage` listener at all. It
 *     does survive a reload of the same tab, and a browser "Duplicate tab"
 *     copies it — both resume the same support session, which is what
 *     isImpersonationTab keys on.
 *   - Using a name distinct from AUTH_STORAGE_KEY ("palate-auth") is a
 *     second, independent guard: even a listener that somehow saw a write
 *     under this key would be filtered out by axios.config.ts's
 *     `event.key !== AUTH_STORAGE_KEY` check at the top of
 *     handleAuthStorageEvent.
 *
 * This only rewires THIS tab's module-level zustand instance (via
 * `persist.setOptions`, the same mechanism axios.config.ts's
 * `supersedeTabSession` already uses to swap in a write-swallowing storage).
 * Every other open tab runs its own separate JS module instance and keeps
 * writing to localStorage exactly as before, completely unaffected.
 *
 * There is deliberately no matching "restore the operator's session" on
 * impersonation end: <ImpersonationBanner />'s Exit button always navigates
 * away to the Ops Panel (a full page unload — see Bug-143 /
 * impersonation-banner.tsx), so there is nothing left in this tab's JS
 * context to restore by the time that matters.
 */
export function scopeAuthStoreToImpersonationTab(): void {
  useAuthStore.persist.setOptions({
    name: IMPERSONATION_AUTH_STORAGE_KEY,
    storage: createJSONStorage(() => sessionStorage),
  });
}
